/* =====================================================================
   casier-plan.js — plans de découpe des casiers (page atelier)
   ---------------------------------------------------------------------
   window.CASIER_PLAN = { DEFAULTS, parse, build, dxf, pieceSvg, elevationSvg, filename }

   Entrée : le lien « _Lien_configuration » d'une commande (…?config=…),
   la valeur base64 seule, ou le JSON « _config » de la commande.
   Sortie : liste des pièces (rectangles) avec leurs usinages, un DXF unique
   (R12, mm, calques par opération) et des aperçus SVG.

   Conventions (validées avec Roman, 28/09/2026) :
   - Haut et bas sur toute la largeur ; côtés, montants entre les deux.
     Avec surélévation (côtés prolongés en pieds), on bascule automatiquement
     en « côtés pleine hauteur » (haut et bas entre les côtés).
   - Montants intermédiaires d'un seul tenant (haut → bas), tablettes
     segmentées par colonne.
   - Fond encastré à l'arrière entre côtés, haut et bas : montants et
     tablettes raccourcis de l'épaisseur du fond en profondeur.
   - Jonction plan / chant par faux tenon (domino V2) : 2 vis dans le plan
     (avant-trous), chant mortaisé, 1 vis traversante dans la pièce mortaisée
     à 18 mm de son chant. 2 dominos par jonction, axes à 50 mm des bords
     avant et arrière (1 seul, centré, si la pièce est trop peu profonde).
   - Tablettes de part et d'autre d'un montant : domino double traversant,
     flottant → mortaise traversante dans le montant, mortaise + vis par
     dessous (18 mm du bord) dans chaque tablette.
   Repère global : X gauche → droite, Y bas → haut (0 = dessous du caisson),
   z = distance depuis la face avant (0 = avant).
   ===================================================================== */
(function (root) {
  'use strict';

  var DEFAULTS = {
    domEdge: 50,          // axe du domino ↔ bord avant / arrière (mm)
    domMinTwo: 130,       // profondeur de pièce mini pour 2 dominos (sinon 1 centré)
    screwSpacing: 13,     // entraxe des 2 vis du domino (mesuré sur domino V2 : ±6,5 mm)
    pilotD: 3,            // avant-trou des vis du domino dans le plan
    pilotDepth: 12,
    crossOffset: 18,      // axe de la vis traversante ↔ chant mortaisé (décalage du domino V2)
    crossD: 5,
    mortL: 24,            // mortaise : longueur (domino 23 mm)
    mortW: 8.5,           // mortaise : largeur (domino 8 mm)
    mortDepth: 27,        // mortaise : profondeur dans le chant (domino dépasse de 26 mm)
    backT: 6,             // épaisseur du fond
    corner: 'topbottom',  // 'topbottom' = haut/bas pleine largeur ; 'sides' = côtés pleine hauteur
    engrave: 1,           // 1 = graver le code de chaque pièce (+ flèche vers l'avant) sur la face usinée
    engraveH: 8,          // hauteur des caractères gravés (mm)
    pegSpacing: 30,       // V58 : côtés pegboard — entraxe de la trame Aire
    pegMargin: 30,        // V58 : centre des trous ↔ bord de la pièce (mini)
    pegD: 6.1,            // V58 : Ø des trous pegboard (comme les panneaux pegboard)
    pegDepth: 12,         // V58 : profondeur des trous (non débouchants, face extérieure) — à valider atelier
    pegClear: 2           // V58 : jeu mini entre un trou pegboard et un usinage de la face intérieure (sinon trou supprimé)
  };

  var r1 = function (n) { return Math.round(n * 10) / 10; };
  var r3 = function (n) { return Math.round(n * 1000) / 1000; };
  var fmt = function (n) { return String(r1(n)).replace('.', ','); };

  /* ---------------------------------------------------------------
     1. Lecture de la configuration
     --------------------------------------------------------------- */
  function b64urlDecode(s) {
    s = s.replace(/-/g, '+').replace(/_/g, '/');
    while (s.length % 4) s += '=';
    if (typeof atob === 'function') return decodeURIComponent(escape(atob(s)));
    return Buffer.from(s, 'base64').toString('utf8');   // node (tests)
  }
  function num(v, d) { var n = parseFloat(v); return isNaN(n) ? d : n; }

  /* Retourne { ok, cfg, error, source } */
  function parse(input) {
    var raw = String(input == null ? '' : input).trim();
    if (!raw) return { ok: false, error: 'empty' };
    var data = null, source = '';
    try {
      if (raw.charAt(0) === '{') {
        data = JSON.parse(raw); source = data.widths_cm ? '_config' : 'json';
      } else {
        var m = raw.match(/[?&]config=([^&#\s]+)/);
        var b = m ? decodeURIComponent(m[1]) : raw.replace(/\s+/g, '');
        if (!/^[A-Za-z0-9_\-+/=]+$/.test(b)) return { ok: false, error: 'format' };
        data = JSON.parse(b64urlDecode(b)); source = m ? 'lien' : 'base64';
      }
    } catch (e) { return { ok: false, error: 'format' }; }
    if (!data || typeof data !== 'object') return { ok: false, error: 'format' };
    var widths = data.widths_cm || data.widths, heights = data.heights_cm || data.heights;
    var cols = Math.round(num(data.cols, 0)), rows = Math.round(num(data.rows, 0));
    if (!Array.isArray(widths) || !Array.isArray(heights) || cols < 1 || rows < 1 || cols > 10 || rows > 10 ||
        widths.length < cols || heights.length < rows) return { ok: false, error: 'content' };
    var cfg = {
      cols: cols, rows: rows,
      widths: widths.slice(0, cols).map(function (v) { return r1(num(v, 0) * 10); }),              // mm, gauche → droite
      heightsTop: heights.slice(0, rows).map(function (v) { return r1(num(v, 0) * 10); }),         // mm, haut → bas
      depth: r1(num(data.depth_cm != null ? data.depth_cm : data.depth, 0) * 10),
      thickness: num(data.thickness_mm != null ? data.thickness_mm : data.thickness, 18),
      feet: r1(num(data.feet_cm != null ? data.feet_cm : data.feet, 0) * 10),
      raise: r1(num(data.raise_cm != null ? data.raise_cm : data.raise, 0) * 10),   // V58 : rehausse des côtés (mm)
      casters: !!data.casters, pieds: !!data.pieds,
      backPanel: (data.back_panel != null ? data.back_panel : data.backPanel) !== false,
      color: (data.locker_color && (data.locker_color.name || data.locker_color.key)) || (data.lockerColor && data.lockerColor.key) || '',
      facades: data.facades && typeof data.facades === 'object' ? Object.keys(data.facades).length : 0,
      // V58 : côtés pegboard (perforations non débouchantes, face extérieure) → les 2 côtés en 21 mm ; pose
      pegL: !!(data.pegL || (data.pegboard_sides && data.pegboard_sides.left)),
      pegR: !!(data.pegR || (data.pegboard_sides && data.pegboard_sides.right)),
      mount: data.mount === 'wall' ? 'wall' : 'floor',
      // V58 : panneaux extérieurs teintés — [{ code: 'G'|'D'|'H'|'B', key }]
      panelColors: (function () {
        var NAMES = { L: 'G', R: 'D', T: 'H', B: 'B', cote_gauche: 'G', cote_droit: 'D', dessus: 'H', dessous: 'B' }, out = [];
        if (Array.isArray(data.panel_colors)) data.panel_colors.forEach(function (x) { if (x && NAMES[x.panel]) out.push({ code: NAMES[x.panel], key: x.key, name: x.name || x.key }); });
        else if (data.panelColors && typeof data.panelColors === 'object') Object.keys(data.panelColors).forEach(function (k) { if (NAMES[k]) out.push({ code: NAMES[k], key: data.panelColors[k], name: data.panelColors[k] }); });
        return out;
      })()
    };
    var sideMm = num(data.side_thickness_mm != null ? data.side_thickness_mm : data.sideThicknessMm, NaN);
    cfg.sideThickness = sideMm > 0 ? sideMm : ((cfg.pegL || cfg.pegR) ? Math.max(cfg.thickness, 21) : cfg.thickness);
    if (cfg.pieds) cfg.feet = 0;
    if (cfg.widths.some(function (w) { return !(w > 0); }) || cfg.heightsTop.some(function (h) { return !(h > 0); }) || !(cfg.depth > 0)) return { ok: false, error: 'content' };
    return { ok: true, cfg: cfg, source: source };
  }

  /* ---------------------------------------------------------------
     2. Pièces et usinages
     Chaque pièce est dessinée dans son repère (u → droite, v → haut,
     origine en bas à gauche), vue depuis la face usinée, posée sur le plateau.
     ops : pilot {u,v} · cross {u,v} · slot {u,v} (mortaise traversante, grand axe horizontal)
           edge {side:'L'|'R'|'B'|'T', at} (mortaise dans le chant, centrée sur « at »)
     --------------------------------------------------------------- */
  function domZ(d, S) { return d < S.domMinTwo ? [d / 2] : [S.domEdge, d - S.domEdge]; }

  function build(cfg, settings) {
    var S = {}, k;
    for (k in DEFAULTS) S[k] = DEFAULTS[k];
    for (k in (settings || {})) if (settings[k] != null && settings[k] !== '') S[k] = (typeof DEFAULTS[k] === 'number') ? num(settings[k], DEFAULTS[k]) : settings[k];

    var C = cfg.cols, R = cfg.rows, T = cfg.thickness, D = cfg.depth, feet = cfg.feet || 0, raise = cfg.raise || 0;
    var TS = cfg.sideThickness || T;                                     // V58 : épaisseur des 2 côtés extérieurs (21 mm si pegboard)
    var w = cfg.widths, hb = cfg.heightsTop.slice().reverse();          // hauteurs de bas en haut
    var W = r1(w.reduce(function (a, b) { return a + b; }, 0) + (C - 1) * T + 2 * TS);
    var Hs = r1(hb.reduce(function (a, b) { return a + b; }, 0) + (R + 1) * T);
    var Dm = cfg.backPanel ? r1(D - S.backT) : D;                        // profondeur montants / tablettes
    var mode = S.corner === 'sides' ? 'sides' : 'topbottom', forced = false;
    if ((feet > 0 || raise > 0) && mode !== 'sides') { mode = 'sides'; forced = true; }   // V58 : rehausse → côtés pleine hauteur aussi
    var half = S.screwSpacing / 2, off = S.crossOffset;

    var xL = [0];                                   // face gauche du montant i (0 = côté gauche, C = côté droit)
    for (var i = 0; i < C; i++) xL.push(r1(xL[i] + (i === 0 ? TS : T) + w[i]));
    var xIn = w.map(function (_, c) { return r1(xL[c] + (c === 0 ? TS : T)); });   // bord gauche intérieur de la colonne c
    var yS = [0];                                   // dessous de la tablette k (0 = bas, R = haut)
    for (var j = 0; j < R; j++) yS.push(r1(yS[j] + T + hb[j]));

    var zFull = domZ(D, S), zIn = domZ(Dm, S);
    var pieces = [], warns = [];

    /* domino vissé sur un plan horizontal (haut / bas) à l'abscisse X, pièce montante de profondeur d */
    function pilotsAlongDepth(ops, u, zs, vOf) { zs.forEach(function (z) { ops.push({ t: 'pilot', u: u, v: vOf(z) - half }, { t: 'pilot', u: u, v: vOf(z) + half }); }); }
    function pilotsAlongU(ops, v, zs, uOf) { zs.forEach(function (z) { ops.push({ t: 'pilot', u: uOf(z) - half, v: v }, { t: 'pilot', u: uOf(z) + half, v: v }); }); }

    /* ---- haut et bas ---- */
    ['bas', 'haut'].forEach(function (which) {
      var top = which === 'haut';
      var x0 = mode === 'sides' ? TS : 0, L = mode === 'sides' ? r1(W - 2 * TS) : W;
      var vOf = top ? function (z) { return D - z; } : function (z) { return z; };   // haut : face inférieure, pièce retournée (avant en haut du dessin)
      var ops = [];
      if (mode === 'topbottom') {
        pilotsAlongDepth(ops, TS / 2, zFull, vOf);
        pilotsAlongDepth(ops, W - TS / 2, zFull, vOf);
      } else {
        zFull.forEach(function (z) {
          ops.push({ t: 'edge', side: 'L', at: vOf(z) }, { t: 'edge', side: 'R', at: vOf(z) });
          ops.push({ t: 'cross', u: off, v: vOf(z) }, { t: 'cross', u: L - off, v: vOf(z) });
        });
      }
      for (var m = 1; m < C; m++) pilotsAlongDepth(ops, xL[m] + T / 2 - x0, zIn, vOf);
      pieces.push({
        id: which, name: top ? 'Haut' : 'Bas', L: L, H: D, T: T, qty: 1,
        face: top ? 'face inférieure (intérieure)' : 'face supérieure (intérieure)',
        labels: { B: top ? 'arrière' : 'AVANT', T: top ? 'AVANT' : 'arrière', L: 'gauche', R: 'droite' },
        ops: ops, kind: 'plateau'
      });
    });

    /* ---- côtés ---- */
    ['G', 'D'].forEach(function (s) {
      var left = s === 'G';
      var y0 = mode === 'sides' ? -feet : T, L = mode === 'sides' ? r1(Hs + feet + raise) : r1(Hs - 2 * T);
      var uOf = function (z, d) { return left ? z : (d - z); };           // gauche : avant à gauche ; droit : avant à droite
      var ops = [];
      if (mode === 'topbottom') {
        zFull.forEach(function (z) {
          var u = uOf(z, D);
          ops.push({ t: 'edge', side: 'B', at: u }, { t: 'edge', side: 'T', at: u });
          ops.push({ t: 'cross', u: u, v: off }, { t: 'cross', u: u, v: L - off });
        });
      } else {
        pilotsAlongU(ops, T / 2 - y0, zFull, function (z) { return uOf(z, D); });
        pilotsAlongU(ops, Hs - T / 2 - y0, zFull, function (z) { return uOf(z, D); });
      }
      for (var sh = 1; sh < R; sh++) pilotsAlongU(ops, yS[sh] + T / 2 - y0, zIn, function (z) { return uOf(z, D); });
      // V58 : perforations pegboard sur la face EXTÉRIEURE (non débouchantes), trame centrée → symétrique, donc
      // identique vue depuis la face intérieure. Trous trop proches d'un usinage intérieur supprimés.
      var peg = left ? cfg.pegL : cfg.pegR, pegOff = 0;
      if (peg) {
        var grid = function (Lg) {
          if (!(Lg >= 2 * S.pegMargin)) return [];
          var n = Math.floor((Lg - 2 * S.pegMargin) / S.pegSpacing + 1e-9) + 1, f0 = (Lg - (n - 1) * S.pegSpacing) / 2, o = [];
          for (var q = 0; q < n; q++) o.push(r1(f0 + q * S.pegSpacing));
          return o;
        };
        var rP = S.pegD / 2 + S.pegClear;
        var clash = function (u, v) {
          return ops.some(function (o) {
            if (o.t === 'pilot') return Math.hypot(o.u - u, o.v - v) < rP + S.pilotD / 2;
            if (o.t === 'cross') return Math.hypot(o.u - u, o.v - v) < rP + S.crossD / 2;
            if (o.t === 'edge') {
              var hl = S.mortL / 2 + rP, dd = S.mortDepth + rP;
              if (o.side === 'B') return Math.abs(u - o.at) < hl && v < dd;
              if (o.side === 'T') return Math.abs(u - o.at) < hl && v > L - dd;
              if (o.side === 'L') return Math.abs(v - o.at) < hl && u < dd;
              if (o.side === 'R') return Math.abs(v - o.at) < hl && u > D - dd;
            }
            return false;
          });
        };
        var pegOps = [];
        grid(D).forEach(function (u) { grid(L).forEach(function (v) { if (clash(u, v)) pegOff++; else pegOps.push({ t: 'peg', u: u, v: v }); }); });
        ops = ops.concat(pegOps);
      }
      pieces.push({
        id: 'cote' + s, name: (left ? 'Côté gauche' : 'Côté droit') + (peg ? ' (pegboard)' : ''), L: D, H: L, T: TS, qty: 1,
        face: 'face intérieure' + (peg ? ' — trous pegboard sur la face EXTÉRIEURE' : ''), peg: !!peg, pegOff: pegOff,
        labels: { L: left ? 'AVANT' : 'arrière', R: left ? 'arrière' : 'AVANT', T: raise > 0 ? 'haut (rehausse ' + fmt(raise / 10) + ' cm)' : 'haut', B: feet > 0 && mode === 'sides' ? 'sol (pied ' + fmt(feet / 10) + ' cm)' : 'bas' },
        ops: ops, kind: 'cote'
      });
    });

    /* ---- montants intermédiaires (identiques entre eux) ---- */
    if (C > 1) {
      var Lm = r1(Hs - 2 * T), opsM = [];
      var uM = function (z) { return Dm - z; };                             // vu depuis la gauche : avant à droite
      zIn.forEach(function (z) {
        opsM.push({ t: 'edge', side: 'B', at: uM(z) }, { t: 'edge', side: 'T', at: uM(z) });
        opsM.push({ t: 'cross', u: uM(z), v: off }, { t: 'cross', u: uM(z), v: Lm - off });
      });
      for (var s2 = 1; s2 < R; s2++) zIn.forEach(function (z) { opsM.push({ t: 'slot', u: uM(z), v: yS[s2] + T / 2 - T }); });
      var names = []; for (var mm = 1; mm < C; mm++) names.push('M' + mm);
      pieces.push({
        id: 'montant', name: 'Montant' + (C > 2 ? 's' : '') + ' ' + names.join(', '), base: 'Montant', refs: names, L: Dm, H: Lm, T: T, qty: C - 1,
        face: 'face gauche (vis traversantes haut / bas de ce côté)',
        labels: { L: 'arrière', R: 'AVANT', T: 'haut', B: 'bas' },
        ops: opsM, kind: 'montant'
      });
    }

    /* ---- tablettes (regroupées par largeur : usinage identique aux deux bouts) ---- */
    if (R > 1) {
      var groups = {}, order = [];
      for (var c = 0; c < C; c++) {
        var key = String(w[c]);
        if (!groups[key]) { groups[key] = { L: w[c], refs: [] }; order.push(key); }
        for (var t = 1; t < R; t++) groups[key].refs.push('C' + (c + 1) + '·T' + t);
      }
      order.forEach(function (key, gi) {
        var g = groups[key], ops = [];
        var vT = function (z) { return Dm - z; };                           // face inférieure, pièce retournée
        zIn.forEach(function (z) {
          ops.push({ t: 'edge', side: 'L', at: vT(z) }, { t: 'edge', side: 'R', at: vT(z) });
          ops.push({ t: 'cross', u: off, v: vT(z) }, { t: 'cross', u: g.L - off, v: vT(z) });
        });
        pieces.push({
          id: 'tablette' + gi, name: 'Tablette' + (g.refs.length > 1 ? 's' : '') + ' ' + g.refs.join(', '), base: 'Tablette', refs: g.refs, L: g.L, H: Dm, T: T, qty: g.refs.length,
          face: 'face inférieure (vis cachées)',
          labels: { T: 'AVANT', B: 'arrière', L: 'gauche', R: 'droite' },
          ops: ops, kind: 'tablette'
        });
      });
    }

    /* ---- fond ---- */
    if (cfg.backPanel) {
      pieces.push({
        id: 'fond', name: 'Fond', L: r1(W - 2 * TS), H: r1(Hs - 2 * T), T: S.backT, qty: 1,
        face: 'face arrière', labels: { T: 'haut', B: 'bas', L: 'droite', R: 'gauche' }, ops: [], kind: 'fond'
      });
    }

    /* ---- codes gravés (un par exemplaire) et position de la gravure ---- */
    var yCell = R > 1 ? (T + yS[1]) / 2 : Hs / 2;              // milieu de l'étage du bas (aucun perçage)
    pieces.forEach(function (p) {
      p.codes = p.id === 'bas' ? ['B'] : p.id === 'haut' ? ['H'] : p.id === 'coteG' ? ['G'] : p.id === 'coteD' ? ['D'] : p.id === 'fond' ? ['F']
        : p.refs.map(function (r) { return r.replace('·', '-'); });
      var front = null, lab = p.labels || {};
      ['B', 'T', 'L', 'R'].forEach(function (k) { if (lab[k] === 'AVANT') front = k; });
      var tw = textWidth(p.codes[0], S.engraveH) + S.engraveH * 1.4;   // code + flèche
      if (front === 'B') p.engr = { u: p.L / 2, v: p.H - 12 - S.engraveH / 2, dir: 'down' };
      else if (front === 'T') p.engr = { u: p.L / 2, v: 12 + S.engraveH / 2, dir: 'up' };
      else if (front === 'L' || front === 'R') {
        var y0 = p.kind === 'cote' && mode === 'sides' ? -feet : T;
        p.engr = { u: front === 'L' ? p.L - 12 - tw / 2 : 12 + tw / 2, v: yCell - y0, dir: front === 'L' ? 'left' : 'right' };
      } else p.engr = { u: p.L / 2, v: 12 + S.engraveH / 2, dir: null };
    });

    /* ---- volumes en position (notice de montage) : X, Y, z en mm ---- */
    var boxes = [], doms = [];
    var box = function (code, kind, x0, x1, y0, y1, z0, z1, extra) { var b = { code: code, kind: kind, x0: x0, x1: x1, y0: y0, y1: y1, z0: z0, z1: z1 }; for (var e in (extra || {})) b[e] = extra[e]; boxes.push(b); };
    if (mode === 'topbottom') {
      box('B', 'bas', 0, W, 0, T, 0, D); box('H', 'haut', 0, W, Hs - T, Hs, 0, D);
      box('G', 'cote', 0, TS, T, Hs - T, 0, D); box('D', 'cote', W - TS, W, T, Hs - T, 0, D);
    } else {
      box('B', 'bas', TS, W - TS, 0, T, 0, D); box('H', 'haut', TS, W - TS, Hs - T, Hs, 0, D);
      box('G', 'cote', 0, TS, -feet, Hs + raise, 0, D); box('D', 'cote', W - TS, W, -feet, Hs + raise, 0, D);
    }
    for (var bm = 1; bm < C; bm++) box('M' + bm, 'montant', xL[bm], xL[bm] + T, T, Hs - T, 0, Dm, { m: bm });
    for (var bc = 0; bc < C; bc++) for (var bk = 1; bk < R; bk++) box('C' + (bc + 1) + '-T' + bk, 'tablette', xIn[bc], xL[bc + 1], yS[bk], yS[bk] + T, 0, Dm, { c: bc + 1, k: bk });
    if (cfg.backPanel) box('F', 'fond', TS, W - TS, T, Hs - T, Dm, D);
    /* dominos : on = pièce qui porte les vis (plan), to = pièce mortaisée ; axis = sens d'emboîtement */
    var DL = 26, DW = 23 / 2, DT = 4;
    var dom = function (type, on, to, axis, x, y, z, sgn) {
      var d = { type: type, on: on, to: to, axis: axis, z0: z - DW, z1: z + DW };
      if (axis === 'y') { d.x0 = x - DT; d.x1 = x + DT; d.y0 = sgn > 0 ? y : y - DL; d.y1 = sgn > 0 ? y + DL : y; }
      else if (type === 'double') { d.x0 = x - T / 2 - DL; d.x1 = x + T / 2 + DL; d.y0 = y - DT; d.y1 = y + DT; }
      else { d.x0 = sgn > 0 ? x : x - DL; d.x1 = sgn > 0 ? x + DL : x; d.y0 = y - DT; d.y1 = y + DT; }
      doms.push(d);
    };
    if (mode === 'topbottom') {
      zFull.forEach(function (z) {
        dom('simple', 'B', 'G', 'y', TS / 2, T, z, 1); dom('simple', 'B', 'D', 'y', W - TS / 2, T, z, 1);
        dom('simple', 'H', 'G', 'y', TS / 2, Hs - T, z, -1); dom('simple', 'H', 'D', 'y', W - TS / 2, Hs - T, z, -1);
      });
    } else {
      zFull.forEach(function (z) {
        dom('simple', 'G', 'B', 'x', TS, T / 2, z, 1); dom('simple', 'D', 'B', 'x', W - TS, T / 2, z, -1);
        dom('simple', 'G', 'H', 'x', TS, Hs - T / 2, z, 1); dom('simple', 'D', 'H', 'x', W - TS, Hs - T / 2, z, -1);
      });
    }
    zIn.forEach(function (z) {
      for (var m2 = 1; m2 < C; m2++) {
        dom('simple', 'B', 'M' + m2, 'y', xL[m2] + T / 2, T, z, 1); dom('simple', 'H', 'M' + m2, 'y', xL[m2] + T / 2, Hs - T, z, -1);
      }
      for (var k2 = 1; k2 < R; k2++) {
        var ym = yS[k2] + T / 2;
        dom('simple', 'G', 'C1-T' + k2, 'x', TS, ym, z, 1);
        dom('simple', 'D', 'C' + C + '-T' + k2, 'x', W - TS, ym, z, -1);
        for (var m3 = 1; m3 < C; m3++) dom('double', 'M' + m3, 'C' + m3 + '-T' + k2 + '|C' + (m3 + 1) + '-T' + k2, 'x', xL[m3] + T / 2, ym, z, 0);
      }
    });

    /* ---- contrôles ---- */
    if (forced) warns.push((feet > 0 ? 'Surélévation de ' + fmt(feet / 10) + ' cm' : '') + (feet > 0 && raise > 0 ? ' et ' : '') + (raise > 0 ? (feet > 0 ? 'rehausse' : 'Rehausse') + ' de ' + fmt(raise / 10) + ' cm au-dessus du haut' : '') + ' : côtés pleine hauteur' + (feet > 0 ? ' prolongés jusqu’au sol' : '') + (raise > 0 ? ' et dépassant du haut' : '') + ', haut et bas entre les côtés (assemblage des angles basculé automatiquement).');
    if (zIn.length < 2 || zFull.length < 2) warns.push('Profondeur faible : un seul domino centré par jonction là où la pièce fait moins de ' + S.domMinTwo + ' mm.');
    if (T - S.mortW < 6) warns.push('Mortaise de ' + fmt(S.mortW) + ' mm dans un chant de ' + T + ' mm : parois de ' + fmt((T - S.mortW) / 2) + ' mm seulement.');
    if (cfg.facades) warns.push(cfg.facades + ' façade(s) (porte / tiroir) dans la commande : non incluses dans ce plan.');
    if (cfg.casters) warns.push('Roulettes : à visser sous le bas (non dessinées).');
    if (cfg.pieds) warns.push('Pieds réglables : à poser sous le bas (non dessinés).');
    if (TS !== T) warns.push('Côtés extérieurs en ' + TS + ' mm (pegboard) ; reste du casier en ' + T + ' mm.');
    pieces.forEach(function (p) {
      if (!p.peg) return;
      var nP = p.ops.filter(function (o) { return o.t === 'peg'; }).length;
      warns.push(p.name + ' : ' + nP + ' trous Ø ' + fmt(S.pegD) + ' prof. ' + S.pegDepth + ' (non débouchants) sur la face EXTÉRIEURE, entraxe ' + S.pegSpacing + ' mm' +
        (p.pegOff ? ' ; ' + p.pegOff + ' trou(s) supprimé(s) car trop proches d’un usinage de la face intérieure (avant-trou, vis, mortaise).' : '.'));
    });
    if (cfg.mount === 'wall') warns.push('Pose suspendue au mur : fixations murales à joindre au colis (non dessinées).');
    if (cfg.backPanel) warns.push('Fond encastré de ' + S.backT + ' mm : montants et tablettes raccourcis à ' + fmt(Dm) + ' mm de profondeur ; fixation du fond non dessinée.');

    /* comptage */
    var count = { dominos: 0, doubles: 0 };
    pieces.forEach(function (p) {
      p.ops.forEach(function (o) {
        if (o.t === 'pilot') count.dominos += 0.5 * p.qty;
        if (o.t === 'slot') count.doubles += p.qty;
      });
    });

    return {
      cfg: cfg, S: S, mode: mode, forcedSides: forced,
      W: W, Hs: Hs, D: D, Dm: Dm, T: T, TS: TS, feet: feet, raise: raise, Htot: r1(Hs + feet + raise), xL: xL, xIn: xIn, yS: yS,
      pieces: pieces, warns: warns, boxes: boxes, doms: doms,
      dominos: Math.round(count.dominos), doubles: count.doubles,
      nPieces: pieces.reduce(function (a, p) { return a + p.qty; }, 0)
    };
  }

  /* ---------------------------------------------------------------
     Police bâton pour la gravure (traits simples, grille 4 × 6)
     --------------------------------------------------------------- */
  var GLYPHS = {
    '0': [[[0, 0], [4, 0], [4, 6], [0, 6], [0, 0]], [[0, 0], [4, 6]]],
    '1': [[[1, 5], [2, 6], [2, 0]], [[1, 0], [3, 0]]],
    '2': [[[0, 5], [1, 6], [3, 6], [4, 5], [4, 4], [0, 0], [4, 0]]],
    '3': [[[0, 6], [4, 6], [4, 0], [0, 0]], [[1, 3], [4, 3]]],
    '4': [[[3, 0], [3, 6], [0, 2], [4, 2]]],
    '5': [[[4, 6], [0, 6], [0, 3], [4, 3], [4, 0], [0, 0]]],
    '6': [[[4, 6], [0, 6], [0, 0], [4, 0], [4, 3], [0, 3]]],
    '7': [[[0, 6], [4, 6], [1, 0]]],
    '8': [[[0, 0], [4, 0], [4, 6], [0, 6], [0, 0]], [[0, 3], [4, 3]]],
    '9': [[[0, 0], [4, 0], [4, 6], [0, 6], [0, 3], [4, 3]]],
    'B': [[[0, 0], [0, 6], [3, 6], [4, 5], [4, 4], [3, 3], [0, 3]], [[3, 3], [4, 2], [4, 1], [3, 0], [0, 0]]],
    'C': [[[4, 6], [0, 6], [0, 0], [4, 0]]],
    'D': [[[0, 0], [0, 6], [2.5, 6], [4, 4.5], [4, 1.5], [2.5, 0], [0, 0]]],
    'F': [[[4, 6], [0, 6], [0, 0]], [[0, 3], [3, 3]]],
    'G': [[[4, 6], [0, 6], [0, 0], [4, 0], [4, 3], [2, 3]]],
    'H': [[[0, 0], [0, 6]], [[4, 0], [4, 6]], [[0, 3], [4, 3]]],
    'M': [[[0, 0], [0, 6], [2, 3], [4, 6], [4, 0]]],
    'T': [[[0, 6], [4, 6]], [[2, 6], [2, 0]]],
    '-': [[[1, 3], [3, 3]]]
  };
  function textWidth(s, h) { return (String(s).length * 6 - 2) * h / 6; }
  /* traits du texte centré en (cx, cy), hauteur h, + flèche vers dir ('up'|'down'|'left'|'right') */
  function strokeText(s, h, cx, cy, dir) {
    var k = h / 6, w = textWidth(s, h), aw = dir ? h * 1.4 : 0, x0 = cx - (w + aw) / 2, y0 = cy - h / 2, out = [];
    String(s).toUpperCase().split('').forEach(function (ch, i) {
      (GLYPHS[ch] || []).forEach(function (st) { out.push(st.map(function (p) { return [x0 + (i * 6 + p[0]) * k, y0 + p[1] * k]; })); });
    });
    if (dir) {
      var ax = x0 + w + h * 0.4 + h / 2, ay = cy, r = h / 2, a = h * 0.3;
      var v = { up: [0, 1], down: [0, -1], left: [-1, 0], right: [1, 0] }[dir], n = [-v[1], v[0]];
      var tip = [ax + v[0] * r, ay + v[1] * r], tail = [ax - v[0] * r, ay - v[1] * r];
      out.push([tail, tip]);
      out.push([[tip[0] - v[0] * a + n[0] * a, tip[1] - v[1] * a + n[1] * a], tip, [tip[0] - v[0] * a - n[0] * a, tip[1] - v[1] * a - n[1] * a]]);
    }
    return out;
  }

  function filename(plan) {
    var c = plan.cfg;
    return 'casier-' + c.cols + 'x' + c.rows + '-' + Math.round(plan.W) + 'x' + Math.round(plan.Htot) + 'x' + Math.round(plan.D) + '-' + c.thickness + 'mm' + (plan.TS !== plan.T ? '-cotes' + plan.TS + 'mm' : '') + (c.pegL || c.pegR ? '-pegboard-' + (c.pegL && c.pegR ? 'GD' : c.pegL ? 'G' : 'D') : '') + '.dxf';
  }

  /* ---------------------------------------------------------------
     3. Disposition (DXF et planche SVG) : pièces en rangées
     --------------------------------------------------------------- */
  function layout(plan) {
    var GAP = 100, TXT = 60, maxRow = Math.max(2400, plan.W + 200);
    var x = 0, y = 0, rowH = 0, placed = [];
    plan.pieces.forEach(function (p) {
      var names = p.refs ? p.refs.map(function (r) { return p.base + ' ' + r; }) : [p.name];
      names.forEach(function (nm, i) {
        if (x > 0 && x + p.L > maxRow) { x = 0; y -= rowH + GAP + TXT; rowH = 0; }
        placed.push({ p: p, name: nm, code: p.codes ? p.codes[i] : '', x: x, y: y - p.H });
        x += p.L + GAP; rowH = Math.max(rowH, p.H);
      });
    });
    return placed;
  }

  /* ---------------------------------------------------------------
     4. DXF (R12 / AC1009, mm) — calques :
        DECOUPE (contours), PILOTE_D{d}_P{p} (avant-trous des dominos, face dessinée),
        VIS_D{d} (vis traversantes, face dessinée, jusqu'à la mortaise),
        MORTAISE_TRAVERSANTE (dominos doubles, oblongs), MORTAISE_CHANT (repère :
        empreinte de la mortaise vue de face + trait d'axe sur le chant), TEXTE.
     --------------------------------------------------------------- */
  function dxf(plan) {
    var S = plan.S, out = [];
    var f = function (n) { return String(r3(n)); };
    var push = function (c, v) { out.push(String(c)); out.push(String(v)); };
    var Lp = 'PILOTE_D' + String(S.pilotD).replace('.', ',') + '_P' + S.pilotDepth;
    var Lv = 'VIS_D' + String(S.crossD).replace('.', ',');
    var Lpeg = 'PEGBOARD_EXT_D' + String(S.pegD).replace('.', ',') + '_P' + S.pegDepth;   // V58 : face extérieure des côtés
    var LAY = [['DECOUPE', 7], [Lp, 1], [Lv, 3], ['MORTAISE_TRAVERSANTE', 5], ['MORTAISE_CHANT', 6], ['GRAVURE', 4], ['TEXTE', 8]];
    if (plan.pieces.some(function (p) { return p.peg; })) LAY.splice(6, 0, [Lpeg, 30]);
    var ascii = function (s) { return String(s).normalize ? String(s).normalize('NFD').replace(/[̀-ͯ]/g, '').replace(/·/g, '.').replace(/[’]/g, "'").replace(/[×]/g, 'x').replace(/[^\x20-\x7e]/g, '') : s; };
    function poly(layer, pts, bulges, closed) {
      push(0, 'POLYLINE'); push(8, layer); push(66, 1); push(70, closed === false ? 0 : 1); push(10, 0); push(20, 0); push(30, 0);
      pts.forEach(function (pt, k) { push(0, 'VERTEX'); push(8, layer); push(10, f(pt[0])); push(20, f(pt[1])); push(30, 0); if (bulges && bulges[k]) push(42, f(bulges[k])); });
      push(0, 'SEQEND'); push(8, layer);
    }
    function circle(layer, x, y, d) { push(0, 'CIRCLE'); push(8, layer); push(10, f(x)); push(20, f(y)); push(30, 0); push(40, f(d / 2)); }
    function text(x, y, h, s, rot) { push(0, 'TEXT'); push(8, 'TEXTE'); push(10, f(x)); push(20, f(y)); push(30, 0); push(40, f(h)); push(1, ascii(s)); if (rot) push(50, rot); }
    var placed = layout(plan);
    var minY = Math.min.apply(null, placed.map(function (q) { return q.y; })) - 40;
    var maxX = Math.max.apply(null, placed.map(function (q) { return q.x + q.p.L; })) + 40;

    push(0, 'SECTION'); push(2, 'HEADER');
    push(9, '$ACADVER'); push(1, 'AC1009');
    push(9, '$INSUNITS'); push(70, 4);
    push(9, '$EXTMIN'); push(10, 0); push(20, f(minY)); push(30, 0);
    push(9, '$EXTMAX'); push(10, f(maxX)); push(20, 140); push(30, 0);
    push(0, 'ENDSEC');
    push(0, 'SECTION'); push(2, 'TABLES');
    push(0, 'TABLE'); push(2, 'LTYPE'); push(70, 1);
    push(0, 'LTYPE'); push(2, 'CONTINUOUS'); push(70, 0); push(3, 'Solid line'); push(72, 65); push(73, 0); push(40, 0);
    push(0, 'ENDTAB');
    push(0, 'TABLE'); push(2, 'LAYER'); push(70, LAY.length + 1);
    push(0, 'LAYER'); push(2, '0'); push(70, 0); push(62, 7); push(6, 'CONTINUOUS');
    LAY.forEach(function (l) { push(0, 'LAYER'); push(2, l[0]); push(70, 0); push(62, l[1]); push(6, 'CONTINUOUS'); });
    push(0, 'ENDTAB');
    push(0, 'ENDSEC');

    push(0, 'SECTION'); push(2, 'ENTITIES');
    var c = plan.cfg;
    text(0, 90, 22, 'Casier ' + c.cols + ' x ' + c.rows + ' - hors-tout ' + fmt(plan.W) + ' x ' + fmt(plan.Htot) + ' x ' + fmt(plan.D) + ' mm - ep. ' + c.thickness + ' mm' + (plan.TS !== plan.T ? ' (cotes ' + plan.TS + ' mm)' : '') + (c.pegL || c.pegR ? ' - pegboard ' + (c.pegL && c.pegR ? 'G+D' : c.pegL ? 'G' : 'D') : '') + (c.mount === 'wall' ? ' - pose murale' : ''));
    if (plan.pieces.some(function (p) { return p.peg; })) text(0, 120, 12, 'PEGBOARD_EXT : trous D ' + S.pegD + ' prof. ' + S.pegDepth + ' NON DEBOUCHANTS sur la face EXTERIEURE des cotes (trame symetrique : meme position vue de l interieur).');
    text(0, 55, 14, 'Dominos simples : ' + plan.dominos + ' - dominos doubles : ' + plan.doubles + ' - ' + plan.nPieces + ' pieces. Chaque piece est vue depuis la face usinee (indiquee sous la piece).');
    if (+S.engrave) text(0, 8, 12, 'GRAVURE : code de la piece + fleche vers le bord AVANT, traits simples (hauteur ' + S.engraveH + ' mm), sur la face usinee, pres du bord arriere.');
    text(0, 30, 14, 'Calques : ' + LAY.map(function (l) { return l[0]; }).join(', ') + '. MORTAISE_CHANT = repere (mortaise ' + S.mortL + ' x ' + S.mortW + ' prof. ' + S.mortDepth + ' dans le chant), ne pas usiner en face.');

    placed.forEach(function (q) {
      var p = q.p, ox = q.x, oy = q.y;
      poly('DECOUPE', [[ox, oy], [ox + p.L, oy], [ox + p.L, oy + p.H], [ox, oy + p.H]]);
      p.ops.forEach(function (o) {
        if (o.t === 'pilot') circle(Lp, ox + o.u, oy + o.v, S.pilotD);
        else if (o.t === 'cross') circle(Lv, ox + o.u, oy + o.v, S.crossD);
        else if (o.t === 'peg') circle(Lpeg, ox + o.u, oy + o.v, S.pegD);
        else if (o.t === 'slot') {
          var a = S.mortL / 2 - S.mortW / 2, rr = S.mortW / 2, cx = ox + o.u, cy = oy + o.v;
          poly('MORTAISE_TRAVERSANTE', [[cx - a, cy - rr], [cx + a, cy - rr], [cx + a, cy + rr], [cx - a, cy + rr]], [0, 1, 0, 1]);
        } else if (o.t === 'edge') {
          var hl = S.mortL / 2, d = S.mortDepth, pts;
          if (o.side === 'B') pts = [[ox + o.at - hl, oy], [ox + o.at + hl, oy], [ox + o.at + hl, oy + d], [ox + o.at - hl, oy + d]];
          if (o.side === 'T') pts = [[ox + o.at - hl, oy + p.H], [ox + o.at + hl, oy + p.H], [ox + o.at + hl, oy + p.H - d], [ox + o.at - hl, oy + p.H - d]];
          if (o.side === 'L') pts = [[ox, oy + o.at - hl], [ox, oy + o.at + hl], [ox + d, oy + o.at + hl], [ox + d, oy + o.at - hl]];
          if (o.side === 'R') pts = [[ox + p.L, oy + o.at - hl], [ox + p.L, oy + o.at + hl], [ox + p.L - d, oy + o.at + hl], [ox + p.L - d, oy + o.at - hl]];
          poly('MORTAISE_CHANT', pts);
        }
      });
      if (+S.engrave && q.code && p.engr) strokeText(q.code, S.engraveH, ox + p.engr.u, oy + p.engr.v, p.engr.dir).forEach(function (pl) { poly('GRAVURE', pl, null, false); });
      var th = 12;
      text(ox, oy - th - 8, th, (q.code ? '[' + q.code + '] ' : '') + q.name);
      text(ox, oy - 2 * th - 14, 8, fmt(p.L) + ' x ' + fmt(p.H) + ' x ' + p.T + ' mm');
      text(ox, oy - 2 * th - 26, 8, 'vue : ' + p.face);
      var lh = Math.max(6, Math.min(12, Math.min(p.L, p.H) / 10)), lab = p.labels || {};
      var inB = hasEdge(p, 'B') ? S.mortDepth + 2 : 0, inT = hasEdge(p, 'T') ? S.mortDepth + 2 : 0;
      var inL = hasEdge(p, 'L') ? S.mortDepth + 2 : 0, inR = hasEdge(p, 'R') ? S.mortDepth + 2 : 0;
      var tw = function (t) { return lh * String(t).length * 0.6; };
      if (lab.B) text(ox + p.L / 2 - tw(lab.B) / 2, oy + 4 + inB, lh, lab.B);
      if (lab.T) text(ox + p.L / 2 - tw(lab.T) / 2, oy + p.H - lh - 4 - inT, lh, lab.T);
      if (lab.L) text(ox + 4 + lh + inL, oy + p.H / 2 - tw(lab.L) / 2, lh, lab.L, 90);
      if (lab.R) text(ox + p.L - 4 - inR, oy + p.H / 2 - tw(lab.R) / 2, lh, lab.R, 90);
    });
    push(0, 'ENDSEC');
    push(0, 'EOF');
    return out.join('\r\n') + '\r\n';
  }
  function hasEdge(p, side) { return p.ops.some(function (o) { return o.t === 'edge' && o.side === side; }); }

  /* ---------------------------------------------------------------
     5. Aperçus SVG
     --------------------------------------------------------------- */
  function esc(s) { return String(s).replace(/&/g, '&amp;').replace(/</g, '&lt;').replace(/>/g, '&gt;'); }

  /* une pièce, cotée, vue depuis sa face usinée */
  function pieceSvg(plan, p, opt) {
    opt = opt || {};
    var S = plan.S, big = Math.max(p.L, p.H), pad = Math.max(34, big / 16), W = p.L + 2 * pad, H = p.H + 2 * pad;
    var k = Math.max(1, big / 450);                                   // grossissement des repères (lisibles en vignette)
    var sc = opt.px ? opt.px / Math.max(W, H) : 1;
    var Y = function (v) { return pad + (p.H - v); };
    var X = function (u) { return pad + u; };
    var fs = Math.max(9, big / 26);
    var s = '<svg xmlns="http://www.w3.org/2000/svg" viewBox="0 0 ' + r1(W) + ' ' + r1(H) + '" width="' + r1(W * sc) + '" height="' + r1(H * sc) + '" font-family="system-ui,-apple-system,sans-serif">';
    s += '<rect x="' + pad + '" y="' + pad + '" width="' + p.L + '" height="' + p.H + '" fill="' + (p.kind === 'fond' ? '#f3efe6' : '#efe6d4') + '" stroke="#1a1a1a" stroke-width="' + r1(Math.max(1, fs / 8)) + '"/>';
    p.ops.forEach(function (o) {
      if (o.t === 'pilot') s += '<circle cx="' + r1(X(o.u)) + '" cy="' + r1(Y(o.v)) + '" r="' + r1(Math.max(S.pilotD / 2, 1.8) * k) + '" fill="#c62828"/>';
      else if (o.t === 'cross') s += '<circle cx="' + r1(X(o.u)) + '" cy="' + r1(Y(o.v)) + '" r="' + r1(S.crossD / 2 * k) + '" fill="#2e7d32"/>';
      else if (o.t === 'peg') s += '<circle cx="' + r1(X(o.u)) + '" cy="' + r1(Y(o.v)) + '" r="' + r1(S.pegD / 2) + '" fill="none" stroke="#ef6c00" stroke-width="' + r1(Math.max(0.8, k * 0.8)) + '"/>';
      else if (o.t === 'slot') s += '<rect x="' + r1(X(o.u) - S.mortL / 2) + '" y="' + r1(Y(o.v) - S.mortW / 2) + '" width="' + S.mortL + '" height="' + S.mortW + '" rx="' + S.mortW / 2 + '" fill="#1565c0"/>';
      else if (o.t === 'edge') {
        var hl = S.mortL / 2, d = S.mortDepth, x, y, w, h;
        if (o.side === 'B') { x = X(o.at) - hl; y = Y(d); w = S.mortL; h = d; }
        if (o.side === 'T') { x = X(o.at) - hl; y = Y(p.H); w = S.mortL; h = d; }
        if (o.side === 'L') { x = X(0); y = Y(o.at) - hl; w = d; h = S.mortL; }
        if (o.side === 'R') { x = X(p.L - d); y = Y(o.at) - hl; w = d; h = S.mortL; }
        s += '<rect x="' + r1(x) + '" y="' + r1(y) + '" width="' + r1(w) + '" height="' + r1(h) + '" fill="rgba(142,36,170,.18)" stroke="#8e24aa" stroke-dasharray="3 2" stroke-width=".8"/>';
      }
    });
    /* repères de bords */
    var lab = p.labels || {}, lf = fs * 0.85;
    if (lab.B) s += '<text x="' + r1(W / 2) + '" y="' + r1(H - pad / 2 + lf / 3) + '" font-size="' + r1(lf) + '" text-anchor="middle" fill="' + (/AVANT/.test(lab.B) ? '#c62828' : '#555') + '" font-weight="' + (/AVANT/.test(lab.B) ? 700 : 400) + '">' + esc(lab.B) + '</text>';
    if (lab.T) s += '<text x="' + r1(W / 2) + '" y="' + r1(pad / 2 + lf / 3) + '" font-size="' + r1(lf) + '" text-anchor="middle" fill="' + (/AVANT/.test(lab.T) ? '#c62828' : '#555') + '" font-weight="' + (/AVANT/.test(lab.T) ? 700 : 400) + '">' + esc(lab.T) + '</text>';
    if (lab.L) s += '<text transform="translate(' + r1(pad / 2 + lf / 3) + ' ' + r1(H / 2) + ') rotate(-90)" font-size="' + r1(lf) + '" text-anchor="middle" fill="' + (/AVANT/.test(lab.L) ? '#c62828' : '#555') + '" font-weight="' + (/AVANT/.test(lab.L) ? 700 : 400) + '">' + esc(lab.L) + '</text>';
    if (lab.R) s += '<text transform="translate(' + r1(W - pad / 2 + lf / 3) + ' ' + r1(H / 2) + ') rotate(-90)" font-size="' + r1(lf) + '" text-anchor="middle" fill="' + (/AVANT/.test(lab.R) ? '#c62828' : '#555') + '" font-weight="' + (/AVANT/.test(lab.R) ? 700 : 400) + '">' + esc(lab.R) + '</text>';
    s += '</svg>';
    return s;
  }

  /* vue de face du casier : pièces et repères (C1…, T1…, M1…) */
  function elevationSvg(plan) {
    var T = plan.T, TS = plan.TS || T, W = plan.W, Hs = plan.Hs, feet = plan.feet, up = plan.raise || 0, pad = 40, Ht = Hs + feet + up;
    var VW = W + 2 * pad, VH = Ht + 2 * pad, fs = Math.max(12, Math.min(W, Ht) / 28);
    var Y = function (y) { return pad + (Hs + up - y); };
    var s = '<svg xmlns="http://www.w3.org/2000/svg" viewBox="0 0 ' + r1(VW) + ' ' + r1(VH) + '" font-family="system-ui,-apple-system,sans-serif">';
    var rect = function (x, y, w, h, fill, label) {
      s += '<rect x="' + r1(pad + x) + '" y="' + r1(Y(y + h)) + '" width="' + r1(w) + '" height="' + r1(h) + '" fill="' + fill + '" stroke="#1a1a1a" stroke-width="1"/>';
      if (label) s += '<text x="' + r1(pad + x + w / 2) + '" y="' + r1(Y(y + h / 2) + fs / 3) + '" font-size="' + r1(fs) + '" text-anchor="middle" fill="#6b6b6b">' + esc(label) + '</text>';
    };
    if (plan.cfg.backPanel) rect(TS, T, W - 2 * TS, Hs - 2 * T, '#f6f2ea');
    var wood = '#d9c29b', i, j;
    if (plan.mode === 'topbottom') {
      rect(0, 0, W, T, wood); rect(0, Hs - T, W, T, wood);
      rect(0, T, TS, Hs - 2 * T, wood); rect(W - TS, T, TS, Hs - 2 * T, wood);
    } else {
      rect(0, -feet, TS, Hs + feet + up, wood); rect(W - TS, -feet, TS, Hs + feet + up, wood);
      rect(TS, 0, W - 2 * TS, T, wood); rect(TS, Hs - T, W - 2 * TS, T, wood);
    }
    for (i = 1; i < plan.cfg.cols; i++) rect(plan.xL[i], T, T, Hs - 2 * T, wood);
    for (j = 1; j < plan.cfg.rows; j++) for (i = 0; i < plan.cfg.cols; i++) rect(plan.xIn[i], plan.yS[j], plan.xL[i + 1] - plan.xIn[i], T, '#e8d7b5');
    /* repères */
    for (i = 0; i < plan.cfg.cols; i++) s += '<text x="' + r1(pad + plan.xIn[i] + (plan.xL[i + 1] - plan.xIn[i]) / 2) + '" y="' + r1(Y(-feet) + fs * 1.3) + '" font-size="' + r1(fs) + '" text-anchor="middle" fill="#0d0d0d">C' + (i + 1) + '</text>';
    for (j = 1; j < plan.cfg.rows; j++) s += '<text x="' + r1(pad - 6) + '" y="' + r1(Y(plan.yS[j] + T / 2) + fs / 3) + '" font-size="' + r1(fs * 0.85) + '" text-anchor="end" fill="#1565c0">T' + j + '</text>';
    for (i = 1; i < plan.cfg.cols; i++) s += '<text x="' + r1(pad + plan.xL[i] + T / 2) + '" y="' + r1(pad - 8) + '" font-size="' + r1(fs * 0.85) + '" text-anchor="middle" fill="#6b6b6b">M' + i + '</text>';
    s += '</svg>';
    return s;
  }

  root.CASIER_PLAN = { DEFAULTS: DEFAULTS, parse: parse, build: build, dxf: dxf, pieceSvg: pieceSvg, elevationSvg: elevationSvg, filename: filename, layout: layout, strokeText: strokeText };
})(typeof window !== 'undefined' ? window : globalThis);
